"use client";

import { useState } from "react";
import {
  arrayUnion,
  doc,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";

type MemberKind = "adult" | "child" | "elderly_parent";

interface AddMemberModalProps {
  onClose: () => void;
  /**
   * Pre-select a kind when opening (e.g. "child" from a CTA labelled
   * "Add child"). Defaults to "adult" otherwise.
   */
  defaultKind?: MemberKind;
}

/**
 * Create a non-auth-linked member profile and append it to the
 * current household's `household_members` array. Mirrors iOS
 * `AddMemberView` (`Views/Members/MemberDashboardView.swift:722-823`).
 *
 * Web previously had no way to add a member that wasn't an existing
 * auth user invited via QR/code — meaning elderly parents and children
 * without their own auth accounts couldn't be represented at all.
 */
export function AddMemberModal({ onClose, defaultKind = "adult" }: AddMemberModalProps) {
  const { householdId, profile } = useHouseholdContext();
  const t = useTranslations();

  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [memberType, setMemberType] = useState<MemberKind>(defaultKind);
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !householdId || !profile?.uid) return;
    setSubmitting(true);
    setError(null);
    try {
      // Generate a stable id from the household + a random suffix so the
      // member doc id is independent of any auth uid (these members do
      // not log in).
      const newId = `mbr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      const memberRef = doc(db, "member_profiles", newId);

      const trimmedNickname = nickname.trim();
      const bm = parseIntOrUndef(birthMonth);
      const bd = parseIntOrUndef(birthDay);
      const by = parseIntOrUndef(birthYear);

      await setDoc(memberRef, {
        name: name.trim(),
        ...(trimmedNickname ? { nickname: trimmedNickname } : {}),
        memberType,
        ...(bm ? { birthMonth: bm } : {}),
        ...(bd ? { birthDay: bd } : {}),
        ...(by ? { birthYear: by } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        isActive: true,
        households: [householdId],
        createdBy: profile.uid,
        createdAt: Timestamp.now(),
      });

      const householdRef = doc(db, "households", householdId);
      await updateDoc(householdRef, {
        household_members: arrayUnion(newId),
      });

      onClose();
    } catch (err) {
      console.error("Failed to add member:", err);
      setError(t("members.add.errorGeneric"));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("common.cancel")}
          </button>
          <h2 className="font-bold text-base">{t("members.add.title")}</h2>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="text-sm font-semibold text-[color:var(--accent-color)] disabled:opacity-50"
          >
            {submitting ? t("members.add.saving") : t("members.add.save")}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <Field label={t("members.add.nameLabel")}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              className="form-input"
              placeholder={t("members.add.namePlaceholder")}
            />
          </Field>

          <Field label={t("members.add.nicknameLabel")}>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="form-input"
              placeholder={t("members.add.nicknamePlaceholder")}
            />
          </Field>

          <Field label={t("members.add.memberTypeLabel")}>
            <div className="flex gap-2">
              {(["adult", "child", "elderly_parent"] as const).map((value) => {
                const active = memberType === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMemberType(value)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color,#00C7BE)] text-white border-[color:var(--accent-color,#00C7BE)]"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700"
                    }`}
                  >
                    {t(`register.memberType.${value}`)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t("members.add.birthdayLabel")}>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="12"
                value={birthMonth}
                onChange={(e) => setBirthMonth(e.target.value)}
                placeholder={t("members.add.birthMonthPlaceholder")}
                className="form-input text-center"
              />
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="31"
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
                placeholder={t("members.add.birthDayPlaceholder")}
                className="form-input text-center"
              />
              <input
                type="number"
                inputMode="numeric"
                min="1900"
                max="2100"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder={t("members.add.birthYearPlaceholder")}
                className="form-input text-center"
              />
            </div>
          </Field>

          <Field label={t("members.add.notesLabel")}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="form-input resize-none"
              placeholder={t("members.add.notesPlaceholder")}
            />
          </Field>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
        </form>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
          background-color: rgb(243 244 246);
          outline: none;
        }
        .form-input:focus {
          box-shadow: 0 0 0 2px var(--accent-color, #00C7BE);
        }
        .dark .form-input {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function parseIntOrUndef(value: string): number | undefined {
  const v = value.trim();
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
